import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { api } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import { usePatientsStore } from "@/stores/patientsStore";
import { useActionItemsStore } from "@/stores/actionItemsStore";
import { useConsentsStore } from "@/stores/consentsStore";
import {
  ImpersonationTarget,
  useImpersonationStore,
} from "@/stores/impersonationStore";

/**
 * Enter and leave "view as user".
 *
 * Switching identity is not just a header flip: every cache in the app is
 * scoped to the *previous* user. Anything not cleared here shows the old user's
 * data under the new user's name, which is exactly the confusion this feature
 * exists to remove.
 */
export function useImpersonation() {
  const queryClient = useQueryClient();
  const setTarget = useImpersonationStore((s) => s.setTarget);
  const clearTarget = useImpersonationStore((s) => s.clear);
  const target = useImpersonationStore((s) => s.target);
  const setUser = useAuthStore((s) => s.setUser);
  const fetchUser = useAuthStore((s) => s.fetchUser);

  /**
   * Drop every cache keyed to the identity we are leaving, and re-point the
   * selected organization at one the *next* identity actually belongs to.
   *
   * `selectedOrganizationId` is persisted in localStorage and survives the
   * switch. Leaving it alone is wrong — if it names an org the next identity is
   * not a member of, useCurrentOrgRole returns null and every role gate
   * degrades silently. But blanking it is also wrong: AppLayout opens the
   * organization picker whenever nothing is selected and more than one org is
   * loaded, so switching identity threw an unexpected modal in your face. Keep
   * it when it is still valid, otherwise fall back to their first org.
   */
  const applyIdentity = useCallback(
    (nextUser: { organizations: { id: string }[] } | null) => {
      queryClient.clear();

      const orgStore = useOrganizationsStore.getState();
      const memberOf = (nextUser?.organizations ?? []).map((o) => o.id);
      const current = orgStore.selectedOrganizationId;
      if (!current || !memberOf.includes(current)) {
        orgStore.setSelectedOrganizationId(memberOf[0] ?? null);
      }

      // setSelectedOrganizationId only clears these when the id actually
      // changes, and we deliberately keep the id when it is still valid — so
      // clear the org-scoped stores explicitly for the identity change itself.
      usePatientsStore.getState().resetPaginationState();
      useActionItemsStore.getState().clearActionItems();
      useConsentsStore.getState().clearConsents();
    },
    [queryClient]
  );

  const startImpersonation = useCallback(
    async (candidate: ImpersonationTarget) => {
      // Opens the audit record and returns the target's /user payload, so the
      // role gates can re-evaluate without a second round trip.
      const impersonatedUser = await api.startImpersonation(candidate.id);
      setTarget(candidate);
      setUser(impersonatedUser);
      applyIdentity(impersonatedUser);
    },
    [applyIdentity, setTarget, setUser]
  );

  const stopImpersonation = useCallback(async () => {
    try {
      await api.stopImpersonation();
    } catch (error) {
      // Closing the audit record is best-effort; failing to close it must not
      // strand the superuser in someone else's account.
      console.error("Failed to close impersonation record:", error);
    }
    clearTarget();
    await fetchUser();
    applyIdentity(useAuthStore.getState().user);
  }, [clearTarget, applyIdentity, fetchUser]);

  return {
    isImpersonating: target !== null,
    target,
    startImpersonation,
    stopImpersonation,
  };
}
